import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";

import UnauthorizedAccess from "components/RequirePermission/UnauthorizedAccess";
import RefreshButton from "components/shared/RefreshButton/RefreshButton";
import { exportErrorMessage } from "pages/admin/components/CopilotDashboard/useCopilotExportJob";
import { formatCostCenterDate } from "pages/ecrOps/costCenters/costCenterDisplay";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetExportJobsQuery, useLazyGetCopilotExportJobDownloadQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useGetCostCenterListQuery } from "state/rtkQuery/user/userQueries";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";
import { showToast } from "utils/reusableToast";

import { ACCENT, bodyCellSx, headCellSx } from "./MeteredParts";

const PER_PAGE = 10;
const POLL_MS = 5000; // while a report is still being built
const STATUSES = [
  ["", "All statuses"],
  ["completed", "Completed"],
  ["running", "Running"],
  ["queued", "Queued"],
  ["failed", "Failed"],
  ["expired", "Expired"],
  ["canceled", "Canceled"],
];
const STATUS_STYLE = {
  completed: { color: ACCENT, background: "rgba(38,137,13,0.1)" },
  running: { color: "#0b5578", background: "#e6f0f7" },
  queued: { color: "#555", background: "#f1f1f1" },
  failed: { color: "#C53211", background: "rgba(197,50,17,0.1)" },
  expired: { color: "#8a5200", background: "#fff4e0" },
  canceled: { color: "#555", background: "#f1f1f1" },
};

const isExpired = (job) => job.status === "expired" || (job.expires_at && new Date(job.expires_at).getTime() <= Date.now());

// Past metered usage reports the signed-in person asked for, with a download for each one still available. Enterprise
// tier: the list isn't narrowed to one organization. Download links expire, so an old report that has expired says so.
const MeteredExportHistory = ({ caps }) => {
  const navigate = useNavigate();
  const { platformId } = useAccessPlatformId();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);

  const { data, isFetching, isError, refetch } = useGetExportJobsQuery(
    { platformId, exportType: "metered_usage", status: status || undefined, page, perPage: PER_PAGE },
    { skip: !platformId || !caps.canViewExport, refetchOnMountOrArgChange: true, pollingInterval: 0 }
  );
  const jobs = data?.data ?? [];
  const total = data?.total ?? 0;
  const active = jobs.some((job) => job.status === "queued" || job.status === "running");
  // While a report is still being built, look again every few seconds so its status and progress stay current.
  useGetExportJobsQuery(
    { platformId, exportType: "metered_usage", status: status || undefined, page, perPage: PER_PAGE },
    { skip: !platformId || !caps.canViewExport || !active, pollingInterval: POLL_MS }
  );

  const { data: orgs } = useGetActiveOrganizationsQuery(undefined, { skip: !platformId });
  const { data: costCenterList } = useGetCostCenterListQuery({ platformId, state: "active" }, { skip: !platformId });
  const orgNames = useMemo(() => Object.fromEntries((orgs ?? []).map((org) => [org._id, org.name])), [orgs]);
  const costCenterNames = useMemo(() => Object.fromEntries((costCenterList ?? []).map((cc) => [cc._id, cc.name])), [costCenterList]);

  const [triggerDownload] = useLazyGetCopilotExportJobDownloadQuery();
  const download = async (job) => {
    setDownloadingId(job.job_id);
    try {
      const { data: info, error } = await triggerDownload({ jobId: job.job_id });
      if (error || !info?.download_url) throw new Error(exportErrorMessage(error, "Failed to get the download link."));
      window.open(info.download_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      showToast({ id: `metered-history-${job.job_id}`, type: "error", title: "Download failed", description: err?.message ?? "Please try again later." });
    } finally {
      setDownloadingId(null);
    }
  };

  if (!caps.canViewExport) {
    return <UnauthorizedAccess message="You do not have permission to view metered usage export history." primaryLabel="Back" primaryTo="/ecr-ops/metered-usage" />;
  }

  const nPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const scopeName = (job) => (job.org_id ? orgNames[job.org_id] ?? "Organization" : "Enterprise");

  return (
    <div>
      <div className="ecr-ops-page-header">
        <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal</p>
        <h1 className="ecr-ops-page-header__title">Metered Usage Export History</h1>
        <p className="ecr-ops-page-header__sub">Reports you have exported before. Download links expire, so older reports may no longer be available.</p>
      </div>

      <div className="ecr-ops-content">
        <Paper elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", p: 2, borderBottom: "1px solid #f0f0f0" }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("/ecr-ops/metered-usage")} sx={{ textTransform: "none", color: "#555" }}>
              Metered Usage
            </Button>
            <Select
              size="small"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              inputProps={{ "aria-label": "Status" }}
              sx={{ height: 34, fontSize: "0.85rem", minWidth: 160 }}
            >
              {STATUSES.map(([value, label]) => (
                <MenuItem key={value || "all"} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>
                {total.toLocaleString("en-US")} report{total === 1 ? "" : "s"}
              </Typography>
              <RefreshButton label="Refresh export history" isRefreshing={isFetching} onClick={refetch} />
            </Box>
          </Box>

          {isFetching && !data ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }} role="status" aria-label="Loading">
              <CircularProgress size={32} sx={{ color: ACCENT }} />
            </Box>
          ) : isError ? (
            <Typography sx={{ p: 4, color: "#C53211", textAlign: "center" }}>
              Couldn't load your export history.{" "}
              <Button size="small" onClick={refetch} sx={{ textTransform: "none", color: ACCENT }}>
                Retry
              </Button>
            </Typography>
          ) : jobs.length === 0 ? (
            <Typography sx={{ p: 5, color: "#999", textAlign: "center", fontStyle: "italic" }}>
              {status ? `No ${status} reports.` : "You haven't exported any metered usage reports yet."}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Requested</TableCell>
                    <TableCell sx={headCellSx}>Scope</TableCell>
                    <TableCell sx={headCellSx}>Cost center</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell sx={headCellSx} align="right">Rows</TableCell>
                    <TableCell sx={headCellSx}>Completed</TableCell>
                    <TableCell sx={headCellSx}>Available until</TableCell>
                    <TableCell sx={headCellSx} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => {
                    const expired = isExpired(job);
                    const shownStatus = job.status === "completed" && expired ? "expired" : job.status;
                    const style = STATUS_STYLE[shownStatus] ?? STATUS_STYLE.queued;
                    const canDownload = job.status === "completed" && !expired && caps.canDownloadExportJob;
                    const busy = downloadingId === job.job_id;
                    return (
                      <TableRow key={job.job_id} data-testid="export-row" sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell sx={{ ...bodyCellSx, fontSize: "0.8rem" }}>{formatCostCenterDate(job.created_at)}</TableCell>
                        <TableCell sx={bodyCellSx}>{scopeName(job)}</TableCell>
                        <TableCell sx={bodyCellSx}>{job.cost_center_id ? costCenterNames[job.cost_center_id] ?? "Cost center" : "All"}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Tooltip title={job.status === "failed" && job.error_message ? job.error_message : ""} arrow>
                            <Chip
                              size="small"
                              label={shownStatus === "running" || shownStatus === "queued" ? `${STATUS_LABEL(shownStatus)} · ${Math.round(job.progress_percent ?? 0)}%` : STATUS_LABEL(shownStatus)}
                              sx={{ fontWeight: 700, fontSize: "0.72rem", color: style.color, backgroundColor: style.background }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }} align="right">{typeof job.rows_processed === "number" ? job.rows_processed.toLocaleString("en-US") : "—"}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontSize: "0.8rem" }}>{job.completed_at ? formatCostCenterDate(job.completed_at) : "—"}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontSize: "0.8rem" }}>{job.expires_at ? formatCostCenterDate(job.expires_at) : "—"}</TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          <Tooltip title={canDownload ? "Download this report as a CSV" : job.status !== "completed" ? "Only completed reports can be downloaded" : expired ? "This report has expired — export it again" : "You don't have permission to download exports"} arrow>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={busy ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
                                disabled={!canDownload || busy}
                                onClick={() => download(job)}
                                sx={{ borderRadius: "16px", textTransform: "none", fontSize: "0.78rem", borderColor: "#ccc", color: "#555", "&:hover": { borderColor: ACCENT, color: ACCENT }, "&.Mui-disabled": { borderRadius: "16px", borderColor: "#e0e0e0", color: "#bbb" } }}
                              >
                                Download
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {nPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 1.5, borderTop: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
              <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} of {total.toLocaleString("en-US")}
              </Typography>
              <Pagination nPages={nPages} currentPage={page} setCurrentPage={setPage} />
            </Box>
          )}
        </Paper>
      </div>
    </div>
  );
};

const STATUS_LABEL = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "Unknown");

export default MeteredExportHistory;
